// src/pages/orderDetail.js

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import api from '../store/api/base';
import Loader from '../components/common/loader';
import { setError, setSuccess } from '../store/slices/uiSlice';
import OrderStatusBadge from '../components/orders/orderStatusBadge';
import OrderTimeline from '../components/orders/orderTimeline';
import ShipmentTracking from '../components/orders/ShipmentTracking';
import {
  trackOrderAsync,
  fetchOrderShipments,
  downloadInvoiceAsync,
  requestReturnAsync,
  clearTrackingData,
  resetReturnSuccess,
  resetInvoiceSuccess
} from '../store/slices/orderSlice';

// ─── Tab IDs ──────────────────────────────────────────────────────────────────
const TABS = {
  DETAILS: 'details',
  TRACKING: 'tracking',
  TIMELINE: 'timeline'
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (dateStr, opts = {}) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...opts
  });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// ─── Return Reason Modal ──────────────────────────────────────────────────────
const ReturnModal = ({ onConfirm, onClose, loading }) => {
  const [reason, setReason] = useState('');
  const RETURN_REASONS = [
    'Damaged / Defective product',
    'Wrong product delivered',
    'Product not as described',
    'Changed my mind',
    'Ordered by mistake',
    'Other'
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold mb-1">Request Return</h3>
        <p className="text-sm text-gray-500 mb-4">
          Select a reason for your return request.
        </p>

        <div className="space-y-2 mb-4">
          {RETURN_REASONS.map(r => (
            <label key={r} className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio"
                name="returnReason"
                value={r}
                checked={reason === r}
                onChange={() => setReason(r)}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-sm text-gray-700">{r}</span>
            </label>
          ))}
        </div>

        <div className="flex justify-end gap-3">
          <button
            className="px-4 py-2 border rounded hover:bg-gray-100 text-sm"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50 text-sm"
            onClick={() => reason && onConfirm(reason)}
            disabled={loading || !reason}
          >
            {loading ? 'Submitting...' : 'Submit Return Request'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Cancel Modal ─────────────────────────────────────────────────────────────
const CancelModal = ({ onConfirm, onClose, loading }) => {
  const [reason, setCancelReason] = useState('');
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold mb-2">Cancel Order</h3>
        <p className="text-gray-600 mb-4 text-sm">
          Are you sure you want to cancel this order? This action cannot be undone.
        </p>

        <label className="block text-sm text-gray-700 mb-2 font-medium">
          Reason for cancellation
        </label>
        <textarea
          value={reason}
          onChange={e => setCancelReason(e.target.value)}
          className="w-full border rounded p-2 h-24 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 mb-4"
          placeholder="I changed my mind..."
        />

        <div className="flex justify-end gap-3">
          <button
            className="px-4 py-2 border rounded hover:bg-gray-100 text-sm"
            onClick={onClose}
            disabled={loading}
          >
            No, Keep Order
          </button>
          <button
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 text-sm"
            onClick={() => onConfirm(reason)}
            disabled={loading || !reason.trim()}
          >
            {loading ? 'Cancelling...' : 'Yes, Cancel Order'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const OrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Local state
  const [order, setOrder] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [activeTab, setActiveTab] = useState(TABS.DETAILS);

  // Redux — shipping/tracking/invoice/return state
  const {
    shipment,
    trackingData,
    loading: orderLoading,
    error: orderError,
    success: orderSuccess
  } = useSelector(state => state.orders);

  // ── Fetch order details ────────────────────────────────────────────────────
  const fetchOrderDetails = useCallback(async () => {
    try {
      setLoadingOrder(true);
      const response = await api.get(`/orders/${orderId}`);
      setOrder(response.data.data.order);
      setLoadingOrder(false);
    } catch (error) {
      dispatch(setError('Failed to load order details'));
      setLoadingOrder(false);
      navigate('/orders');
    }
  }, [orderId, dispatch, navigate]);

  useEffect(() => {
    fetchOrderDetails();
    // Clean up tracking data when leaving page
    return () => dispatch(clearTrackingData());
  }, [fetchOrderDetails, dispatch]);

  // ── Auto-fetch shipment info for shipped/delivered orders ─────────────────
  useEffect(() => {
    if (!order) return;
    const shippedStatuses = ['SHIPPED', 'DELIVERED', 'PROCESSING', 'RETURNED'];
    if (shippedStatuses.includes(order.status)) {
      dispatch(fetchOrderShipments(orderId));
    }
  }, [order?.status, orderId, dispatch]);

  // ── Return success feedback ───────────────────────────────────────────────
  useEffect(() => {
    if (orderSuccess.return) {
      dispatch(setSuccess('Return request submitted successfully!'));
      setShowReturnModal(false);
      fetchOrderDetails();
      dispatch(resetReturnSuccess());
    }
  }, [orderSuccess.return, dispatch, fetchOrderDetails]);

  // ── Invoice success feedback ──────────────────────────────────────────────
  useEffect(() => {
    if (orderSuccess.invoice) {
      dispatch(setSuccess('Invoice downloaded successfully!'));
      dispatch(resetInvoiceSuccess());
    }
  }, [orderSuccess.invoice, dispatch]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleTrackOrder = () => {
    setActiveTab(TABS.TRACKING);
    dispatch(trackOrderAsync(orderId));
  };

  const handleDownloadInvoice = () => {
    dispatch(downloadInvoiceAsync(orderId));
  };

  const handleCancelOrder = async (reason) => {
    if (!reason.trim()) {
      dispatch(setError('Please provide a reason for cancellation'));
      return;
    }
    try {
      setCancelling(true);
      await api.post(`/orders/${orderId}/cancel`, { reason });
      dispatch(setSuccess('Order cancelled successfully'));
      setShowCancelModal(false);
      fetchOrderDetails();
    } catch (error) {
      dispatch(setError(error.response?.data?.message || 'Failed to cancel order'));
    } finally {
      setCancelling(false);
    }
  };

  const handleReturnSubmit = (reason) => {
    dispatch(requestReturnAsync({ orderId, reason }));
  };

  // ── Loading & not-found states ─────────────────────────────────────────────
  if (loadingOrder && !order) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center">
        <Loader size="lg" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-yellow-50 p-4 rounded text-yellow-800">
          Order not found. <Link to="/orders" className="underline text-blue-600">View all orders</Link>
        </div>
      </div>
    );
  }

  // ── Derived flags ──────────────────────────────────────────────────────────
  const isShipped    = ['SHIPPED', 'DELIVERED', 'OUT_FOR_DELIVERY'].includes(order.status);
  const isDelivered = order.status === 'DELIVERED' && order.isInvoiceAvailable === true;

  const isCancelled  = order.status === 'CANCELLED';
  const isReturnable = isDelivered; // Can request return if delivered

  // Check if within return window (7 days from delivery)
  const withinReturnWindow = (() => {
    if (!isDelivered || !order.updatedAt) return false;
    const daysSinceDelivery = (Date.now() - new Date(order.updatedAt)) / (1000 * 60 * 60 * 24);
    return daysSinceDelivery <= 7;
  })();

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
        <div>
          <Link to="/orders" className="text-blue-600 hover:underline text-sm">
            ← Back to Orders
          </Link>
          <h1 className="text-2xl font-bold mt-1">
            Order <span className="text-gray-500 font-normal">#{order.orderId}</span>
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Placed on {formatDateTime(order.createdAt)}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <OrderStatusBadge status={order.status} />

          {/* Track button — shown for shipped/processing orders */}
          {isShipped && (
            <button
              onClick={handleTrackOrder}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              🚚 Track Shipment
            </button>
          )}

          {/* Download invoice — only for delivered orders */}
          {isDelivered && (
            <button
              onClick={handleDownloadInvoice}
              disabled={orderLoading.invoice}
              className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {orderLoading.invoice ? '⏳ Downloading...' : '📄 Download Invoice'}
            </button>
          )}

          {/* Return button — only for delivered orders within 7 days */}
          {isReturnable && withinReturnWindow && order.status !== 'RETURNED' && (
            <button
              onClick={() => setShowReturnModal(true)}
              className="px-3 py-1.5 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors"
            >
              ↩️ Request Return
            </button>
          )}

          {/* Cancel button */}
          {order.canBeCancelled && !isCancelled && (
            <button
              onClick={() => setShowCancelModal(true)}
              className="px-3 py-1.5 text-sm text-red-600 border border-red-300 rounded-md hover:bg-red-50 transition-colors"
            >
              Cancel Order
            </button>
          )}
        </div>
      </div>

      {/* ── Estimated delivery banner ──────────────────────────────────────── */}
      {order.estimatedDelivery && !isDelivered && !isCancelled && (
        <div className="mb-5 bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 flex items-center gap-3">
          <span className="text-xl">🗓️</span>
          <div>
            <p className="text-sm font-semibold text-blue-800">
              Estimated Delivery:{' '}
              {formatDate(order.estimatedDelivery, { weekday: 'short' })}
            </p>
            <p className="text-xs text-blue-500 mt-0.5">
              Delivery window set by Shiprocket · Actual date may vary slightly
            </p>
          </div>
        </div>
      )}

      {/* ── Shipment courier info bar (if shipment exists) ─────────────────── */}
      {shipment && isShipped && (
        <div className="mb-5 bg-white border rounded-lg px-4 py-3 flex flex-wrap items-center gap-4 shadow-sm text-sm">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Courier</p>
            <p className="font-semibold text-gray-800">{shipment.courier?.name || 'Shiprocket'}</p>
          </div>
          {(shipment.courier?.trackingId || shipment.courier?.waybillNumber) && (
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">AWB / Tracking ID</p>
              <p className="font-mono font-semibold text-gray-800">
                {shipment.courier?.trackingId || shipment.courier?.waybillNumber}
              </p>
            </div>
          )}
          {shipment.courier?.trackingUrl && (
            <a
              href={shipment.courier.trackingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Track on Courier Site ↗
            </a>
          )}
        </div>
      )}

      {/* ── Tabs ────────────────────────────────────────────────────────────── */}
      <div className="flex border-b mb-6">
        {[
          { id: TABS.DETAILS,  label: '📦 Order Details' },
          { id: TABS.TRACKING, label: '🚚 Shipment Tracking' },
          { id: TABS.TIMELINE, label: '🕐 Timeline' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              if (tab.id === TABS.TRACKING && !trackingData && !orderLoading.tracking) {
                dispatch(trackOrderAsync(orderId));
              }
            }}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Tab Content ─────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">

          {/* ── DETAILS TAB ────────────────────────────────────────────────── */}
          {activeTab === TABS.DETAILS && (
            <div className="space-y-6">
              {/* Order items */}
              <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                <div className="p-4 border-b bg-gray-50">
                  <h2 className="text-base font-semibold">Order Items</h2>
                </div>
                <div className="divide-y">
                  {order.items?.map((item, index) => (
                    <div key={`${item.productId}-${index}`} className="p-4 flex gap-4">
                      <div className="w-16 h-16 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                        <img
                          src={item.imageUrl || item.thumbnailUrl || '/images/placeholder.png'}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-grow">
                        <Link
                          to={`/product/${item.productId}`}
                          className="font-medium hover:text-blue-600 text-sm"
                        >
                          {item.name}
                        </Link>
                        {item.color && (
                          <p className="text-xs text-gray-500 mt-0.5">Color: {item.color}</p>
                        )}
                        <div className="flex justify-between items-end mt-2">
                          <span className="text-sm text-gray-500">Qty: {item.quantity}</span>
                          <div className="text-right">
                            <span className="font-semibold text-sm">
                              ₹{(item.price * item.quantity).toLocaleString('en-IN')}
                            </span>
                            {item.mrp > item.price && (
                              <span className="text-xs text-gray-400 line-through ml-2">
                                ₹{(item.mrp * item.quantity).toLocaleString('en-IN')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Addresses */}
              <div className="bg-white rounded-lg shadow-sm border p-5">
                <h2 className="text-base font-semibold mb-4">Addresses</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {[
                    { label: 'Shipping Address', addr: order.shippingAddress },
                    { label: 'Billing Address',  addr: order.billingAddress }
                  ].map(({ label, addr }) => (
                    addr && (
                      <div key={label}>
                        <h3 className="text-sm font-medium text-gray-600 mb-1">{label}</h3>
                        <div className="text-sm text-gray-700 space-y-0.5">
                          <p className="font-semibold">{addr.fullName}</p>
                          <p>{addr.addressLine1}</p>
                          {addr.addressLine2 && <p>{addr.addressLine2}</p>}
                          {addr.landmark && <p className="text-gray-500">Near: {addr.landmark}</p>}
                          <p>{addr.city}, {addr.state} — {addr.pincode}</p>
                          <p className="text-gray-500">📞 {addr.phone}</p>
                        </div>
                      </div>
                    )
                  ))}
                </div>
              </div>

              {/* Order notes */}
              {order.notes && (
                <div className="bg-white rounded-lg shadow-sm border p-5">
                  <h2 className="text-base font-semibold mb-2">Order Notes</h2>
                  <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">{order.notes}</p>
                </div>
              )}
            </div>
          )}

          {/* ── TRACKING TAB ───────────────────────────────────────────────── */}
          {activeTab === TABS.TRACKING && (
            <div className="bg-white rounded-lg shadow-sm border p-5">
              <h2 className="text-base font-semibold mb-4">Shipment Tracking</h2>
              {!isShipped && !shipment ? (
                <div className="text-center py-10">
                  <div className="text-4xl mb-3">📦</div>
                  <p className="text-gray-600 font-medium">Not yet shipped</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Tracking will be available once your order is picked up by the courier.
                  </p>
                </div>
              ) : (
                <ShipmentTracking
                  shipment={shipment}
                  trackingData={trackingData}
                  loading={orderLoading.tracking}
                  error={orderError.tracking}
                />
              )}
            </div>
          )}

          {/* ── TIMELINE TAB ───────────────────────────────────────────────── */}
          {activeTab === TABS.TIMELINE && (
            <div className="bg-white rounded-lg shadow-sm border p-5">
              <h2 className="text-base font-semibold mb-4">Order & Shipment Timeline</h2>
              <OrderTimeline
                timeline={order.timeline}
                shipmentTimeline={
                  shipment?.timeline ||
                  trackingData?.trackingHistory?.map(t => ({
                    timestamp: t.date,
                    status: t.status,
                    description: t.activity,
                    location: t.location
                  }))
                }
              />
            </div>
          )}
        </div>

        {/* ── Sidebar: Order Summary ─────────────────────────────────────── */}
        <div>
          <div className="bg-white rounded-lg shadow-sm border p-5 sticky top-20 space-y-3">
            <h2 className="text-base font-semibold mb-2">Order Summary</h2>

            <div className="text-sm space-y-2 text-gray-600">
              <div className="flex justify-between">
                <span>Order Date</span>
                <span className="text-gray-800">{formatDate(order.createdAt)}</span>
              </div>

              {/* Estimated delivery — from Shiprocket */}
              {order.estimatedDelivery && !isDelivered && (
                <div className="flex justify-between items-start">
                  <span>Est. Delivery</span>
                  <span className="text-right">
                    <span className="text-blue-700 font-semibold text-xs block">
                      {formatDate(order.estimatedDelivery, { weekday: 'short' })}
                    </span>
                    <span className="text-xs text-gray-400">via Shiprocket</span>
                  </span>
                </div>
              )}

              {/* Actual delivery date if delivered */}
              {isDelivered && shipment?.actualDelivery && (
                <div className="flex justify-between">
                  <span>Delivered On</span>
                  <span className="text-green-700 font-semibold text-xs">
                    {formatDate(shipment.actualDelivery, { weekday: 'short' })}
                  </span>
                </div>
              )}

              <div className="flex justify-between">
                <span>Payment</span>
                <span className="text-gray-800">
                  {order.payment?.method === 'COD' ? 'Cash on Delivery' : 'Online (PhonePe)'}

                </span>
              </div>

              <div className="flex justify-between items-center">
                <span>Payment Status</span>
                <OrderStatusBadge status={order.payment?.status} />
              </div>
            </div>

            <hr />

            {/* Pricing */}
            <div className="text-sm space-y-2">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span>
                <span>₹{order.pricing?.subTotal?.toLocaleString('en-IN') || 0}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Shipping</span>
                <span>
                  {order.pricing?.shippingCost > 0
                    ? `₹${order.pricing.shippingCost.toLocaleString('en-IN')}`
                    : <span className="text-green-600">Free</span>}
                </span>
              </div>
              {order.pricing?.gst > 0 && (
                <div className="flex justify-between text-gray-600">
                  <span>GST (18%)</span>
                  <span>₹{order.pricing.gst.toLocaleString('en-IN')}</span>
                </div>
              )}
              {order.pricing?.discount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Discount</span>
                  <span>−₹{order.pricing.discount.toLocaleString('en-IN')}</span>
                </div>
              )}
              <div className="flex justify-between font-bold pt-2 border-t text-base">
                <span>Total</span>
                <span>₹{order.pricing?.totalAmount?.toLocaleString('en-IN') || 0}</span>
              </div>
            </div>

            {/* Invoice download shortcut in sidebar */}
            {isDelivered && (
              <button
                onClick={handleDownloadInvoice}
                disabled={orderLoading.invoice}
                className="w-full mt-2 py-2 text-sm border border-green-500 text-green-700 rounded-md hover:bg-green-50 disabled:opacity-50 transition-colors"
              >
                {orderLoading.invoice ? '⏳ Downloading...' : '📄 Download Invoice (PDF)'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Modals ─────────────────────────────────────────────────────────── */}
      {showCancelModal && (
        <CancelModal
          onConfirm={handleCancelOrder}
          onClose={() => setShowCancelModal(false)}
          loading={cancelling}
        />
      )}

      {showReturnModal && (
        <ReturnModal
          onConfirm={handleReturnSubmit}
          onClose={() => setShowReturnModal(false)}
          loading={orderLoading.return}
        />
      )}
    </div>
  );
};

export default OrderDetail;